/**
 * iter318 wave 17 — Emergent Google Sign-in button + callback handler.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Two exports:
 *   • GoogleSignInButton — starts the OAuth dance. Redirects to
 *     `auth.emergentagent.com` with `redirect={window.location.origin + returnPath}`.
 *   • useGoogleAuthCallback — hook that runs synchronously during
 *     App render. If the URL hash contains `session_id=…`, it POSTs
 *     the id to the correct backend endpoint (tenant vs global based
 *     on `route_intent`) and finishes the sign-in. Uses a ref (not
 *     state) to guard against StrictMode double-invocation per the
 *     Emergent Auth playbook.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// iter318 wave 20 (2026-02) — POST same-origin (matches services/api.js).
// The build-time `REACT_APP_BACKEND_URL` points at `www.vyomos.io`,
// but every tenant subdomain proxies `/api/*` to the same backend
// via Cloudflare; using `window.location.origin` avoids the cross-
// origin preflight that CORS blocks on tenant hosts (the ARM
// "CORS Edge Fix" platform ticket is still open for edge preflights).
const API = (typeof window !== "undefined" && window.location
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL);

export function GoogleSignInButton({ returnPath = '/', dataTestId = 'google-sign-in-btn', label = 'Continue with Google', className = '' }) {
  const handleClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + returnPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={dataTestId}
      className={`w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-full border border-[#DCE6F0] bg-white hover:bg-[#F8FBFF] hover:border-[#55AEEF] text-[#10233F] font-semibold text-sm transition ${className}`}
    >
      <GoogleIcon />
      {label}
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.87 2.68-6.62z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A9 9 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.32z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58z"/>
    </svg>
  );
}

/**
 * Reads window.location.hash for `session_id=…` and exchanges it.
 * `mode` is either 'tenant' or 'global':
 *   • 'tenant' → POST /api/auth/google/tenant-login → refresh app
 *   • 'global' → POST /api/auth/google/global-lookup → render tenant picker
 *
 * `onGlobalChoices({email, choices})` is called after global lookup
 * so the caller can render a tenant-picker UI.
 */
export function useGoogleAuthCallback({ mode, onGlobalChoices, onTenantAuthenticated }) {
  const processed = useRef(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const hash = window.location.hash || '';
    if (!hash.includes('session_id=')) return;
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    // Clean the fragment immediately so a refresh doesn't retry the flow.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    setProcessing(true);

    const endpoint = mode === 'global'
      ? '/api/auth/google/global-lookup'
      : '/api/auth/google/tenant-login';

    axios.post(`${API}${endpoint}`, { session_id: sessionId }, { withCredentials: true })
      .then((r) => {
        if (mode === 'global') {
          onGlobalChoices && onGlobalChoices(r.data);
        } else {
          onTenantAuthenticated && onTenantAuthenticated(r.data);
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.detail || 'Google sign-in failed.';
        setError(msg);
        toast.error(msg);
      })
      .finally(() => setProcessing(false));
  }, []);

  return { processing, error };
}
